import { POLICY_PACK } from "../data/cdmPolicyPack";
import type { ApprovalRecord, ProjectWorkspace, RegulatoryRegion } from "../types/domain";
import { blankWorkspace, upgradeWorkspace } from "./projectStore";
import { requireSupabase } from "./supabase";

export interface CloudOrganization {
  id: string;
  name: string;
  role: "owner" | "admin" | "editor" | "reviewer" | "viewer";
}

export interface CloudProject {
  id: string;
  organizationId: string;
  name: string;
  workspace: ProjectWorkspace;
  updatedAt: string;
}

interface MembershipRow {
  organization_id: string;
  role: CloudOrganization["role"];
}

interface OrganizationRow {
  id: string;
  name: string;
}

interface ProjectRow {
  id: string;
  organization_id: string;
  name: string;
  workspace: unknown;
  updated_at: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseCloudWorkspace(value: unknown): ProjectWorkspace {
  if (!isRecord(value) || !isRecord(value.project)) {
    throw new Error("The saved project workspace is not valid.");
  }
  if (!Array.isArray(value.risks) || !Array.isArray(value.evidence)) {
    throw new Error("The saved project collections are not valid.");
  }
  if (typeof value.project.id !== "string" || typeof value.project.name !== "string") {
    throw new Error("The saved project identity is not valid.");
  }
  const workspace = upgradeWorkspace(value as unknown as Partial<ProjectWorkspace>);
  const validRegions: RegulatoryRegion[] = [
    "unresolved",
    "peninsular-malaysia",
    "sabah",
    "sarawak",
    "labuan",
  ];
  return {
    ...workspace,
    project: {
      ...workspace.project,
      regulatoryRegion: validRegions.includes(workspace.project.regulatoryRegion)
        ? workspace.project.regulatoryRegion
        : "unresolved",
    },
  };
}

export async function loadPrimaryOrganization(
  userId: string,
): Promise<CloudOrganization | null> {
  const client = requireSupabase();
  const { data: membership, error: membershipError } = await client
    .from("organization_memberships")
    .select("organization_id, role")
    .eq("user_id", userId)
    .eq("active", true)
    .order("joined_at", { ascending: true })
    .limit(1)
    .maybeSingle();

  if (membershipError) throw membershipError;
  if (!membership) return null;

  const typedMembership = membership as MembershipRow;
  const { data: organization, error: organizationError } = await client
    .from("organizations")
    .select("id, name")
    .eq("id", typedMembership.organization_id)
    .single();

  if (organizationError) throw organizationError;
  const typedOrganization = organization as OrganizationRow;
  return {
    id: typedOrganization.id,
    name: typedOrganization.name,
    role: typedMembership.role,
  };
}

export async function createOrganization(name: string): Promise<string> {
  const client = requireSupabase();
  const { data, error } = await client.rpc("create_organization", {
    organization_name: name.trim(),
  });
  if (error) throw error;
  if (typeof data !== "string") throw new Error("Organization creation did not return an ID.");
  return data;
}

export async function loadCloudProjects(
  organizationId: string,
): Promise<CloudProject[]> {
  const client = requireSupabase();
  const { data, error } = await client
    .from("projects")
    .select("id, organization_id, name, workspace, updated_at")
    .eq("organization_id", organizationId)
    .neq("status", "archived")
    .order("updated_at", { ascending: false });

  if (error) throw error;

  return ((data ?? []) as ProjectRow[]).map((row) => ({
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    workspace: parseCloudWorkspace(row.workspace),
    updatedAt: row.updated_at,
  }));
}

export async function createCloudProject(
  organizationId: string,
  userId: string,
  name: string,
  initialWorkspace?: ProjectWorkspace
): Promise<CloudProject> {
  const client = requireSupabase();
  const workspace = initialWorkspace ? { ...initialWorkspace } : blankWorkspace();
  if (!initialWorkspace) {
    workspace.project.name = name.trim();
  }
  workspace.project.updatedAt = new Date().toISOString();

  const { data, error } = await client
    .from("projects")
    .insert({
      organization_id: organizationId,
      name: workspace.project.name,
      client_name: "",
      location: "",
      status: "active",
      policy_pack_version: POLICY_PACK.version,
      workspace,
      created_by: userId,
    })
    .select("id, organization_id, name, workspace, updated_at")
    .single();

  if (error) throw error;
  const row = data as ProjectRow;
  return {
    id: row.id,
    organizationId: row.organization_id,
    name: row.name,
    workspace: parseCloudWorkspace(row.workspace),
    updatedAt: row.updated_at,
  };
}

export async function saveCloudProject(
  projectId: string,
  workspace: ProjectWorkspace,
): Promise<string> {
  const client = requireSupabase();
  const { data, error } = await client
    .from("projects")
    .update({
      name: workspace.project.name.trim() || "Untitled project",
      client_name: workspace.project.clientName.trim() || null,
      location: workspace.project.location.trim() || null,
      policy_pack_version: POLICY_PACK.version,
      workspace,
    })
    .eq("id", projectId)
    .select("updated_at")
    .single();

  if (error) throw error;
  return (data as { updated_at: string }).updated_at;
}

export async function persistApprovalDecision(
  projectId: string,
  organizationId: string,
  userId: string,
  approval: ApprovalRecord,
): Promise<void> {
  if (approval.status === "requested") return;
  const client = requireSupabase();
  const { error } = await client.from("approval_decisions").insert({
    id: approval.id,
    project_id: projectId,
    organization_id: organizationId,
    entity_type: approval.entityType,
    entity_key: approval.entityId,
    decision: approval.status,
    step: approval.step,
    comment: approval.comment,
    decided_by: userId,
    decided_at: approval.decidedAt || new Date().toISOString(),
  });
  if (error && error.code !== "23505") throw error;
}

export async function deleteCloudProject(projectId: string): Promise<void> {
  const client = requireSupabase();
  const { error } = await client
    .from("projects")
    .delete()
    .eq("id", projectId);

  if (error) throw new Error("Could not delete project: " + error.message);
}
